import PropTypes from "prop-types";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ReferenceLine,
  Label,
  Brush,
  ResponsiveContainer,
} from "recharts";

import { CustomizedTick } from "./components/xAxisTick";
import { CustomTooltip } from "./components/tooltip";
import { option_colors } from "../../service/option";
import { decimalAddZero, cleanFloat } from "../../service/common";

import { classNames } from "primereact/utils";

function YAxisTick({ x, y, payload, xyLabelColor, isTenThousandUnit }) {
  return (
    <text
      x={x}
      y={y}
      fill={xyLabelColor}
      textAnchor="end"
      dominantBaseline="middle"
    >
      {typeof payload.value === "number"
        ? decimalAddZero({
            rawVal: cleanFloat(
              (payload.value ?? 0) / (isTenThousandUnit ? 10000 : 1),
            ),
            decimalShowRaw: true,
          })
        : payload.value}
    </text>
  );
}

function formatUnit({ unit, isTenThousandUnit }) {
  if (!unit) return "";
  return `${isTenThousandUnit ? "萬" : ""}${unit}`;
}

function getAxisTypeProps({ layout, dataKey }) {
  if (layout === "vertical") return { dataKey, type: "category" };
  return { type: "number" };
}

function getReferenceLabelPosition(axis) {
  if (axis === "x") return "top";
  return "insideBottomRight";
}

function YAxisTickRenderer(props) {
  return <YAxisTick {...props} />;
}

export default function ModuleChartLine({
  className = "",
  styles = {},
  data = { isTenThousandUnit: false, keys: [], data: [] },
  COLORS = option_colors,
  options = {},
}) {
  const {
    layout = "horizontal",
    dataKey = "label",
    xAxisOptions = {
      isBottom: true,
      isTop: false,
      label: {},
      unit: {},
    },
    yAxisOptions = {
      isLeft: true,
      isRight: false,
      label: {},
      unit: {},
    },
    xyLabelColor = "#a3a3a3",
    isControlDomain = false,
    margin = {
      top: 40,
      right: 30,
      left: 20,
      bottom: 10,
    },
    lineSize = 2,
    isLineDot = false,
    isTooltip = true,
    tooltipKey = null,
    tooltipOptions = {},
    isLegend = true,
    isBrush = false,
  } = options;
  const yLeftUnit = formatUnit({
    unit: yAxisOptions.unit?.left,
    isTenThousandUnit: data.isTenThousandUnit,
  });

  return (
    <div className={classNames("inline-block", className)} style={styles}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data.data} margin={margin}>
          <XAxis
            dataKey={dataKey}
            stroke={xyLabelColor}
            label={xAxisOptions.label?.bottom ?? ""}
            tick={<CustomizedTick color={xyLabelColor} />}
          />

          {yAxisOptions.isLeft !== false && (
            <YAxis
              yAxisId="left"
              orientation="left"
              stroke={xyLabelColor}
              unit={yLeftUnit}
              tick={
                <YAxisTickRenderer
                  xyLabelColor={xyLabelColor}
                  isTenThousandUnit={data.isTenThousandUnit}
                />
              }
              {...getAxisTypeProps({ layout, dataKey })}
              {...(isControlDomain ? { domain: ["auto", "auto"] } : {})}
              label={yAxisOptions.label?.left ?? ""}
            />
          )}
          {yAxisOptions.isRight && (
            <YAxis
              yAxisId="right"
              orientation="right"
              stroke={xyLabelColor}
              unit={yAxisOptions.unit?.right ?? null}
              {...getAxisTypeProps({ layout, dataKey })}
              {...(isControlDomain ? { domain: ["auto", "auto"] } : {})}
              label={yAxisOptions.label?.right ?? ""}
            />
          )}

          {isTooltip && (
            <Tooltip
              cursor={{ fill: "#ffffff00" }}
              content={
                <CustomTooltip
                  isTenThousandUnit={data.isTenThousandUnit}
                  tooltipKey={tooltipKey}
                  tooltipOptions={tooltipOptions}
                />
              }
            />
          )}
          {isLegend && <Legend wrapperStyle={{ paddingTop: 5 }} />}
          {data.keys.map((item, kIdx) => (
            <Line
              key={item.key}
              yAxisId={item.yAxisId ?? "left"}
              dataKey={item.key}
              name={item.name ?? item.key}
              stroke={item.color ?? COLORS[kIdx]}
              strokeWidth={lineSize}
              strokeDasharray={item.strokeDasharray}
              activeDot={{ r: 4 }}
              dot={isLineDot}
            />
          ))}

          {data.referenceKey?.map((item, index) => (
            <ReferenceLine
              key={`refer_${item.axis ?? "y"}-${item.value}-${item.label ?? index}`}
              yAxisId={item.yAxisId ?? "left"}
              {...{ [item.axis === "x" ? "x" : "y"]: item.value }}
              stroke={item.color ?? "red"}
              strokeDasharray="5 5"
              ifOverflow="extendDomain"
            >
              {item.label && (
                <Label
                  value={item.label}
                  fill={item.color ?? xyLabelColor}
                  position={getReferenceLabelPosition(item.axis)}
                  offset={10}
                />
              )}
            </ReferenceLine>
          ))}

          {isBrush && <Brush />}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

YAxisTick.propTypes = {
  x: PropTypes.number,
  y: PropTypes.number,
  payload: PropTypes.shape({
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  xyLabelColor: PropTypes.string,
  isTenThousandUnit: PropTypes.bool,
};

YAxisTickRenderer.propTypes = {
  ...YAxisTick.propTypes,
};

ModuleChartLine.propTypes = {
  className: PropTypes.string,
  styles: PropTypes.object,
  data: PropTypes.shape({
    isTenThousandUnit: PropTypes.bool,
    keys: PropTypes.arrayOf(
      PropTypes.shape({
        key: PropTypes.string,
        name: PropTypes.string,
        color: PropTypes.string,
        yAxisId: PropTypes.string,
        strokeDasharray: PropTypes.string,
      }),
    ),
    data: PropTypes.arrayOf(PropTypes.object),
    referenceKey: PropTypes.arrayOf(
      PropTypes.shape({
        axis: PropTypes.string,
        value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        label: PropTypes.string,
        color: PropTypes.string,
        yAxisId: PropTypes.string,
      }),
    ),
  }),
  COLORS: PropTypes.arrayOf(PropTypes.string),
  options: PropTypes.object,
};
